/**
 * Upload de documents + gestion du pipeline d'ingestion.
 * Déclenche PlanValidator quand plan_pending.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "../../api/client";
import { useDocumentStore } from "../../store";
import PlanValidator from "./PlanValidator";
import PipelineStepper from "./PipelineStepper";

const ACCEPTED_FORMATS = ".pdf,.docx,.html,.pptx,.csv,.xlsx,.txt,.md";
const POLLING_INTERVAL = 3000;

export default function FileUploader() {
  const { documents, loadDocuments, uploadDocument, triggerEmbedding, triggerGraphing } = useDocumentStore();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [planDoc, setPlanDoc] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef({});

  useEffect(() => {
    loadDocuments();
    return () => Object.values(pollRef.current).forEach(clearInterval);
  }, []);

  // Surveiller les documents en cours de traitement
  useEffect(() => {
    documents.forEach((doc) => {
      if (!["done", "error"].includes(doc.status) && !pollRef.current[doc.id]) {
        pollRef.current[doc.id] = setInterval(() => pollStatus(doc), POLLING_INTERVAL);
      }
    });
  }, [documents]);

  const pollStatus = async (doc) => {
    try {
      const { data } = await api.get(`/api/documents/${doc.id}/status/`);
      if (data.status === "plan_pending") {
        clearInterval(pollRef.current[doc.id]);
        // Charger le plan complet
        const { data: planData } = await api.get(`/api/documents/${doc.id}/plan/`);
        setPlanDoc({ ...doc, validated_plan: planData.plan });
      } else if (["chunked", "embedded", "done", "error"].includes(data.status)) {
        clearInterval(pollRef.current[doc.id]);
        delete pollRef.current[doc.id];
        loadDocuments();
      }
    } catch {}
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) await handleFiles(files);
  };

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length) await handleFiles(files);
  };

  const handleFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      const ext = file.name.split(".").pop().toLowerCase();
      try {
        await uploadDocument(file, {
          title: file.name.replace(/\.[^.]+$/, ""),
          file_format: ext,
          security_level: 1,
        });
      } catch (e) {
        alert(`Erreur upload ${file.name}: ${e.message}`);
      }
    }
    setUploading(false);
    await loadDocuments();
  };

  return (
    <div className="file-uploader">
      {/* Zone de drop */}
      <div
        className={`drop-zone ${dragging ? "drop-zone--active" : ""}`}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FORMATS}
          multiple
          onChange={handleFileInput}
          style={{ display: "none" }}
        />
        {uploading ? (
          <p>⏳ Upload en cours…</p>
        ) : (
          <>
            <p>📁 Glissez vos fichiers ici ou cliquez</p>
            <small>PDF, DOCX, HTML, PPTX, CSV, XLSX</small>
          </>
        )}
      </div>

      {/* Liste des documents */}
      <div className="document-list">
        {documents.map((doc) => (
          <DocumentCard
            key={doc.id}
            doc={doc}
            onValidatePlan={() => {
              api.get(`/api/documents/${doc.id}/plan/`).then(({ data }) =>
                setPlanDoc({ ...doc, validated_plan: data.plan })
              );
            }}
            onTriggerEmbedding={async () => { await triggerEmbedding(doc.id); loadDocuments(); }}
            onTriggerGraphing={async () => { await triggerGraphing(doc.id); loadDocuments(); }}
          />
        ))}
      </div>

      {/* PlanValidator modal (EX-2) */}
      {planDoc && (
        <div className="modal-overlay">
          <div className="modal">
            <PlanValidator
              doc={planDoc}
              onApproved={() => { setPlanDoc(null); loadDocuments(); }}
              onDismiss={() => setPlanDoc(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentCard({ doc, onValidatePlan, onTriggerEmbedding, onTriggerGraphing }) {
  const statusIcons = {
    uploaded: "📄", parsing: "⚙️", plan_pending: "⚠️",
    plan_approved: "✅", chunking: "✂️", chunked: "📦",
    embedding: "🔢", embedded: "🧬", graphing: "🕸️",
    done: "✅", error: "❌",
  };

  return (
    <div className={`doc-card doc-card--${doc.status}`}>
      <span className="doc-card__icon">{statusIcons[doc.status] || "📄"}</span>
      <div className="doc-card__info">
        <strong>{doc.title}</strong>
        <span className="doc-card__format">{doc.file_format?.toUpperCase()}</span>
        <PipelineStepper status={doc.status} />
      </div>
      {doc.status === "plan_pending" && (
        <button className="btn btn--warning" onClick={onValidatePlan} title="Rouvre l'éditeur — n'approuve rien">
          Reprendre l'édition du plan ⚠️
        </button>
      )}
      {doc.status === "chunked" && (
        <button className="btn btn--primary" onClick={onTriggerEmbedding}>
          Lancer la vectorisation 🔢
        </button>
      )}
      {doc.status === "embedded" && (
        <button className="btn btn--primary" onClick={onTriggerGraphing}>
          Construire le graphe 🕸️
        </button>
      )}
      {doc.status === "error" && doc.error_message && (
        <span className="doc-card__error" title={doc.error_message}>⚠️</span>
      )}
    </div>
  );
}
